import type { ReactNode } from "react";

interface BookCover3DProps {
  /** Whatever renders the flat artwork (BookCover, BookCoverLoading, …). */
  children: ReactNode;
  className?: string;
}

/**
 * "Solid book" presentation for the library grid.
 *
 * Wraps the existing cover artwork — unchanged — in a real CSS 3D frame: the
 * board is turned a few degrees so a bound spine shows down the left edge and
 * the block of pages shows along the top, giving the card physical thickness
 * instead of a flat rectangle.
 *
 * Purely decorative: it carries no text and no interactive affordance, so the
 * title, metadata, and buttons underneath are untouched. `aria-hidden` is NOT
 * set because the artwork inside still carries the book's alt text.
 *
 * All depth styling lives in `.lumi-book3d-*` (globals.css) so the transform
 * chain stays in one place and can be flattened under
 * `prefers-reduced-motion`.
 */
export default function BookCover3D({ children, className }: BookCover3DProps) {
  return (
    <div className={`lumi-book3d ${className ?? ""}`}>
      <div className="lumi-book3d-stage">
        {/* Bound spine — swings back from the left edge of the front board. */}
        <div className="lumi-book3d-spine" />
        {/* Block of pages, just inset from the front board. */}
        <div className="lumi-book3d-pages" />
        <div className="lumi-book3d-board">
          {children}
          {/* Gutter shading where the board meets the spine, plus a thin
              highlight along the fore-edge. Both are pure decoration. */}
          <div className="lumi-book3d-gutter" />
        </div>
      </div>
    </div>
  );
}
